import { isEmpty, isNil } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBSearchBar } from '@shared/components/searchBar';
import { bounceIn, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MenuItem } from './MenuItem';
import { MenuScrollable } from './MenuScrollable';
import { MenuSection } from './MenuSection';
import styles from './MenuOptions.module.css';

/**
 * Menu.Options / Menu.SearchableOptions
 *
 * Renders a generic list of selectable `Menu.Item` rows from an array of items.
 *
 * Selected items are marked with a checked checkbox via `isItemSelected`. If
 * `multiple` is set, unselected items will show an empty checkbox. If
 * `isItemSelected` is not provided, no markers will be shown.
 *
 * Rows that need a body of their own pass `getItemContent`; Use `renderItem` if
 * you need to customize rendering of the whole row.
 *
 * `Menu.SearchableOptions` wraps `Menu.Options` with a search bar that filters
 * items client-side by label. Use it when the option list is long enough that
 * users benefit from filtering.
 */

const SEARCH_THRESHOLD = 10;

interface MenuOptionsProps<T> {
  options: T[];
  getItemKey: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemIcon?: (item: T) => MBIconV2Name | undefined | 'none';
  /** Leading content for a row that carries an image rather than an icon — an avatar, a thumbnail. */
  getItemStartContent?: (item: T) => ReactNode;
  onSelect?: (item: T) => void;
  isItemSelected?: (item: T) => boolean;
  isItemDisabled?: (item: T) => boolean;
  /**
   * Custom body for a row — a second line, a pill, a tag swatch — rendered as the item’s children
   * in place of its label. The menu still owns key, icon, click, marker and disabled state.
   */
  getItemContent?: (item: T) => ReactNode;
  /**
   * The whole row, for rows that aren’t a `Menu.Item` at all — unlike `getItemContent` the callback
   * owns the key, click, icon, marker and disabled state, and every prop driving them is ignored.
   */
  renderItem?: (item: T) => ReactNode;
  /** Marks rows with a checkbox instead of a checkmark, for menus that allow multiple selection. */
  multiple?: boolean;
  /**
   * Shown in place of the rows when there are none — a menu that opens onto nothing collapses to a
   * sliver that reads as failed-to-open. Defaults to "No results"; pass one to say something better.
   */
  emptyLabel?: string;
}

export function MenuOptions<T>({
  options,
  getItemKey,
  onSelect,
  isItemSelected,
  getItemLabel,
  getItemIcon,
  getItemStartContent,
  getItemContent,
  isItemDisabled,
  renderItem,
  multiple = false,
  emptyLabel,
}: MenuOptionsProps<T>) {
  const Marker = multiple ? CheckboxMarker : SelectedMarker;

  if (isEmpty(options)) {
    return <EmptyOptions label={emptyLabel} />;
  }

  return (
    <MenuScrollable showScrollIndicators>
      {isNil(renderItem) &&
        options.map(option => {
          const label = getItemLabel(option);
          const content = getItemContent?.(option);

          return (
            <MenuItem
              key={getItemKey?.(option)}
              icon={getItemIcon?.(option)}
              startContent={getItemStartContent?.(option)}
              // `RowContent` renders `label` *or* `children`, so a row with its
              // own content should not pass label
              label={isNil(content) ? label : undefined}
              aria-label={label}
              onClick={() => onSelect?.(option)}
              disabled={isItemDisabled?.(option)}
              endContent={isItemSelected && <Marker isSelected={isItemSelected(option)} />}
            >
              {content}
            </MenuItem>
          );
        })}
      {!isNil(renderItem) && options.map(renderItem)}
    </MenuScrollable>
  );
}

export function SelectedMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  return (
    <View
      key={key}
      center
      style={{ width: grid(1.5) }}
      className={[styles.selectedMarker, isSelected && bounceIn]}
    >
      {isSelected && <MBIconV2 name="check-box" size={grid(3.5)} />}
    </View>
  );
}

export function CheckboxMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  const icon = isSelected ? 'check-box' : 'check-box-empty';
  return (
    <View key={key} center className={[styles.selectedMarker, isSelected && bounceIn]}>
      <MBIconV2 name={icon} size={grid(3.5)} />
    </View>
  );
}

interface SearchableOptionsProps<T> extends MenuOptionsProps<T> {
  title?: string;
}

export function SearchableOptions<T>({
  title,
  options,
  onSelect,
  isItemSelected,
  getItemKey,
  getItemLabel,
  getItemIcon,
  getItemStartContent,
  getItemContent,
  isItemDisabled,
  renderItem,
  multiple,
  emptyLabel,
}: SearchableOptionsProps<T>) {
  const [filter, setFilter] = useState('');

  const filteredOptions = useMemo(() => {
    return options.filter(option => {
      const label = getItemLabel(option);
      return label.toLowerCase().includes(filter.toLowerCase());
    });
  }, [filter, options, getItemLabel]);

  const showSearch = options.length > SEARCH_THRESHOLD;
  const showHeader = !isNil(title) || showSearch;

  return (
    <>
      {showHeader && (
        <MenuSection title={title}>
          {showSearch && <MBSearchBar value={filter} onChangeText={setFilter} />}
        </MenuSection>
      )}
      <MenuOptions
        options={filteredOptions}
        onSelect={onSelect}
        isItemSelected={isItemSelected}
        getItemKey={getItemKey}
        getItemLabel={getItemLabel}
        getItemIcon={getItemIcon}
        getItemStartContent={getItemStartContent}
        getItemContent={getItemContent}
        isItemDisabled={isItemDisabled}
        renderItem={renderItem}
        multiple={multiple}
        emptyLabel={emptyLabel}
      />
    </>
  );
}

/**
 * Menu.EmptyOptions
 *
 * The row a menu shows in place of its options when it has none — a menu that opens onto nothing
 * collapses to a sliver that reads as failed-to-open. `Menu.Options` renders this itself; use it
 * directly in a menu that keeps its own list, so the two read the same.
 */

interface EmptyOptionsProps {
  /** Defaults to "No results"; pass one where the menu can say something more useful. */
  label?: string;
  icon?: MBIconV2Name;
}

export function EmptyOptions({ label, icon = 'exclamation-outline' }: EmptyOptionsProps) {
  return <MenuItem disabled icon={icon} iconSize={grid(4)} label={label ?? i18n('common.empty')} />;
}
